// src/platform/components/ContactSubmissions.jsx
import React, { useState, useEffect } from 'react';
import { Mail, Clock, CheckCircle, MessageCircle, Eye, Calendar } from 'lucide-react';
import { ContactService } from '../../shared/services/ContactService';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

const ContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'new', 'read', 'responded'
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [submissionsData, statsData] = await Promise.all([
        ContactService.getAllSubmissions(),
        ContactService.getSubmissionStats()
      ]);
      
      setSubmissions(submissionsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading contact data:', err);
      setError('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (submissionId) => {
    try {
      await ContactService.markAsRead(submissionId);
      // Refresh data
      await loadData();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAsResponded = async (submissionId, response) => {
    try {
      await ContactService.markAsResponded(submissionId, response);
      // Refresh data
      await loadData();
      setSelectedSubmission(null);
    } catch (err) {
      console.error('Error marking as responded:', err);
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filter === 'all') return true;
    return submission.status === filter;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <Mail className="w-4 h-4 text-blue-500" />;
      case 'read':
        return <Eye className="w-4 h-4 text-yellow-500" />;
      case 'responded':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-yellow-100 text-yellow-800';
      case 'responded':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadData}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Submissions</h2>
          <p className="text-gray-600">Manage inquiries from your landing page</p>
        </div>
        
        <div className="flex gap-4">
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats?.total || 0}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{stats?.new || 0}</div>
            <div className="text-sm text-gray-600">New</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats?.responded || 0}</div>
            <div className="text-sm text-gray-600">Responded</div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        {['all', 'new', 'read', 'responded'].map((filterOption) => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
              filter === filterOption
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {filterOption}
            {filterOption !== 'all' && stats && (
              <span className="ml-1 bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {stats[filterOption]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {filteredSubmissions.length === 0 ? (
          <div className="p-12 text-center">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions found</h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No contact submissions yet.'
                : `No ${filter} submissions.`
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission) => (
              <div key={submission.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {getStatusIcon(submission.status)}
                      <h3 className="font-medium text-gray-900">{submission.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                        {submission.status}
                      </span>
                      {submission.product === 'walmart_routing' && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Routing HQ
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 mb-3">
                      <div>
                        <span className="font-medium">Email:</span> {submission.email}
                      </div>
                      {submission.company && (
                        <div>
                          <span className="font-medium">Company:</span> {submission.company}
                        </div>
                      )}
                      {submission.industry && (
                        <div>
                          <span className="font-medium">Industry:</span> {submission.industry}
                        </div>
                      )}
                      {submission.vendorNumbers && (
                        <div>
                          <span className="font-medium">Vendor #s:</span> {submission.vendorNumbers}
                        </div>
                      )}
                    </div>

                    {submission.message && (
                      <div className="text-sm text-gray-800 mb-3 bg-gray-50 p-3 rounded-lg">
                        {submission.message}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {submission.timestamp?.toLocaleDateString()} {submission.timestamp?.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {submission.status === 'new' && (
                      <button
                        onClick={() => handleMarkAsRead(submission.id)}
                        className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm hover:bg-yellow-200"
                      >
                        Mark as Read
                      </button>
                    )}
                    
                    {(submission.status === 'new' || submission.status === 'read') && (
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm hover:bg-green-200"
                      >
                        Mark as Responded
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Response Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Mark as Responded
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a note about how you responded to <strong>{selectedSubmission.name}</strong>:
            </p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const response = e.target.response.value;
              handleMarkAsResponded(selectedSubmission.id, response);
            }}>
              <textarea
                name="response"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                placeholder="e.g., Replied via email with pricing information..."
                required
              />
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setSelectedSubmission(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Mark as Responded
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissions;